import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';

import type { Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMatch, useSearchParams } from 'react-router';
import { Box, useTheme } from '@/shared/ui';

import { captureEvent } from '@/services/analytics';

import type { CatalogItem } from '@/entities/catalog-item';
import { extractExistingParams } from '../../lib/extractExistingParams';
import ActiveFilterChips from './ActiveFilterChips';
import CatalogTableGrid from './CatalogTableGrid/CatalogTableGrid';
import CatalogToolbar from './CatalogToolbar';
import { FILTER_DRAWER_TRANSITION_DURATION, FILTER_DRAWER_WIDTH } from './constants';
import TableFilters from './TableFilters/TableFilters';

const CatalogTable = ({
  filterOpen,
  setFilterOpen,
  table,
  isForwardCurves,
  expandedData,
  setExpandedData,
  setShowDiscontinued,
  showDiscontinued,
  renderFactsheetButton
}: {
  filterOpen: boolean;
  setFilterOpen: (filterOpen: boolean) => void;
  table: Table<CatalogItem>;
  isForwardCurves: boolean;
  expandedData: boolean;
  setExpandedData: (newValue: boolean) => void;
  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}) => {
  const { transitions } = useTheme();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isInDashboard = Boolean(useMatch('/dashboard/*'));
  const [searchParams] = useSearchParams();
  const rows = table.getRowModel().rows;

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- TanStack Virtual powers catalog row windowing.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 23,
    getScrollElement: () => tableContainerRef.current,
    overscan: 5
  });

  useEffect(() => {
    captureEvent('catalogue_filter', extractExistingParams(searchParams));
  }, [searchParams]);

  return (
    <>
      <CatalogToolbar
        expandedData={expandedData}
        filterOpen={filterOpen}
        isForwardCurves={isForwardCurves}
        setExpandedData={setExpandedData}
        setFilterOpen={setFilterOpen}
        setShowDiscontinued={setShowDiscontinued}
        showDiscontinued={showDiscontinued}
        table={table}
      />
      <ActiveFilterChips />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <TableFilters
          filterOpen={filterOpen}
          height='100%'
          setFilterOpen={setFilterOpen}
          table={table}
        />
        <Box
          sx={{
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            ml: filterOpen ? 0 : `${-FILTER_DRAWER_WIDTH}px`,
            transition: transitions.create('margin-left', {
              easing: transitions.easing.easeInOut,
              duration: FILTER_DRAWER_TRANSITION_DURATION
            })
          }}
        >
          {rows.length ? (
            <CatalogTableGrid
              isForwardCurves={isForwardCurves}
              isInDashboard={isInDashboard}
              renderFactsheetButton={renderFactsheetButton}
              rowVirtualizer={rowVirtualizer}
              rows={rows}
              table={table}
              tableContainerRef={tableContainerRef}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              Nothing to display here. Try changing your filters.
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CatalogTable;
